// Pure helpers for reconciling the user-scoped, cloud-synced collections — the
// My Tournaments folders (state.tournamentOrg) and the saved settings templates
// (state.templates) — between this device's copy and the copy in the user doc.
//
// No DOM, no state.js, no firebase, so it stays trivially unit-testable
// (tests/user-data-merge.test.js).
//
// WHY A MERGE AND NOT LAST-WRITER-WINS
// The user doc is written with merge:true from each tab's IN-MEMORY state, and
// Firestore replaces array fields wholesale. So a second tab or device that has
// been open since before a folder was created overwrites the whole `folders`
// array with its stale copy on its next save — registering one result is
// enough — and the folder silently disappears. Unioning by id on load means the
// device that still holds the folder re-publishes what the stale writer dropped,
// so the loss heals itself instead of being permanent.
//
// WHY TOMBSTONES
// A plain union would resurrect anything deleted on another device: the deleting
// side no longer has the entry, so the other side's copy always "wins" the
// union. Each delete therefore records a { id, at } tombstone that rides along
// in the same synced payload, and the merge drops any id tombstoned on either
// side. Tombstones are pruned by age and count so they can't grow unbounded.

// Tombstones older than this are dropped — long enough that every device has
// certainly synced the delete, short enough that the list stays tiny.
export const TOMBSTONE_MAX_AGE_MS = 180 * 24 * 60 * 60 * 1000; // 180 days
export const TOMBSTONE_MAX = 200;

function asArray(v) {
    return Array.isArray(v) ? v : [];
}

function asObject(v) {
    return v && typeof v === 'object' && !Array.isArray(v) ? v : {};
}

function idOf(entry) {
    if (!entry || typeof entry !== 'object') return '';
    const raw = entry.id;
    return raw === undefined || raw === null ? '' : String(raw);
}

// A delete marker for `id`, stamped now (or at `at` for tests/replays).
export function makeTombstone(id, at = Date.now()) {
    return { id: String(id), at };
}

// Union two tombstone lists (newest stamp per id wins), then prune by age and
// count. Order-independent: merging A into B gives the same result as B into A.
export function mergeTombstones(a, b, now = Date.now()) {
    const byId = new Map();
    asArray(a).concat(asArray(b)).forEach(t => {
        const id = idOf(t);
        if (!id) return;
        const at = Number(t.at) || 0;
        const prev = byId.get(id);
        if (!prev || at > prev.at) byId.set(id, { id, at });
    });
    return Array.from(byId.values())
        .filter(t => now - t.at < TOMBSTONE_MAX_AGE_MS)
        .sort((x, y) => y.at - x.at)
        .slice(0, TOMBSTONE_MAX);
}

// Union two id-carrying lists, dropping every tombstoned id.
//
// The winning side supplies the field values for ids present in BOTH lists (a
// rename or pin toggle is still last-writer-wins — only existence is unioned)
// and its ordering; ids only the other side has are appended. `preferLocal`
// picks the local copy as the winner, for when this device has changes that
// never reached the cloud (see the cloudSyncPending flag in firebase-auth.js).
export function mergeListById(localList, cloudList, deleted, { preferLocal = false } = {}) {
    const dead = new Set(asArray(deleted).map(idOf).filter(Boolean));
    const winner = preferLocal ? asArray(localList) : asArray(cloudList);
    const other = preferLocal ? asArray(cloudList) : asArray(localList);

    const out = [];
    const seen = new Set();
    winner.concat(other).forEach(entry => {
        const id = idOf(entry);
        if (!id || seen.has(id) || dead.has(id)) return;
        seen.add(id);
        out.push(entry);
    });
    return out;
}

// Union two id-keyed record maps (state.tournamentOrg.items). Keys present on
// both sides take the winning side's record; keys only one side has survive.
// No tombstones: a per-tournament record is never deleted, only cleared
// (folderId: null), so an absent key always means "this side never saw it".
export function mergeItemMaps(localItems, cloudItems, { preferLocal = false } = {}) {
    const local = asObject(localItems);
    const cloud = asObject(cloudItems);
    return preferLocal ? { ...cloud, ...local } : { ...local, ...cloud };
}

// Reconcile a whole tournamentOrg payload. Returns a fresh, fully-shaped object;
// neither input is mutated.
export function mergeTournamentOrg(local, cloud, opts = {}) {
    const l = asObject(local);
    const c = asObject(cloud);
    const deletedFolders = mergeTombstones(l.deletedFolders, c.deletedFolders, opts.now);
    return {
        folders: mergeListById(l.folders, c.folders, deletedFolders, opts),
        items: mergeItemMaps(l.items, c.items, opts),
        deletedFolders
    };
}
